import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, ShoppingBag, Edit2, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import MobileNav from '../components/MobileNav';
import AddSpendingModal from '../components/AddSpendingModal';
import EditSpendingModal from '../components/EditSpendingModal';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

// Use window.location.origin for API calls to work on any domain
const API = `${window.location.origin}/api`;

export default function SpendingPage() {
  const { user } = useAuth();
  const currency = user?.currency || 'GBP';
  const [spending, setSpending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSpending, setEditingSpending] = useState(null);
  const [deletingSpending, setDeletingSpending] = useState(null);

  const fetchSpending = async () => {
    try {
      const response = await fetch(`${API}/spending`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSpending(data);
      }
    } catch (error) {
      toast.error('Failed to load spending');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpending();
  }, []);

  const handleDelete = async () => {
    if (!deletingSpending) return;

    try {
      const response = await fetch(`${API}/spending/${deletingSpending.spending_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Spending deleted');
        fetchSpending();
        setDeletingSpending(null);
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const totalSpending = spending.reduce((sum, s) => sum + s.amount, 0);

  // Group spending by date
  const groupedSpending = spending.reduce((groups, item) => {
    const date = new Date(item.date).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'short'
    });
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1224] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#63E6E2]/30 border-t-[#63E6E2] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1224] page-container" data-testid="spending-page">
      {/* Header */}
      <header className="px-4 pt-4 pb-2 safe-top">
        <h1 className="font-heading text-2xl font-bold text-white">Unplanned Spending</h1>
        <p className="text-[#6B7280]">Expenses added outside your regular bills</p>
      </header>

      <main className="px-4 py-4 space-y-6">
        {/* Summary Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#6B7280] text-sm">Total Spent This Period</p>
              <p className="font-heading text-3xl font-bold text-white" data-testid="total-spending-amount">
                {formatCurrency(totalSpending, currency)}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-[#EF4444]/10 flex items-center justify-center">
              <ShoppingBag className="w-6 h-6 text-[#EF4444]" />
            </div>
          </div>
        </motion.div>

        {/* Add Spending Button */}
        <Button
          onClick={() => setShowAddModal(true)}
          className="w-full h-12 bg-[#63E6E2] text-[#0F1224] hover:bg-[#4DD4CF] font-semibold"
          data-testid="add-spending-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Spending
        </Button>

        {/* Spending List */}
        <div className="space-y-6">
          {Object.keys(groupedSpending).length > 0 ? (
            Object.entries(groupedSpending).map(([date, items], groupIndex) => (
              <motion.div
                key={date}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: groupIndex * 0.1 }}
              >
                <h3 className="text-[#6B7280] text-sm font-medium mb-3">{date}</h3>
                <div className="space-y-2">
                  {items.map((item) => (
                    <div
                      key={item.spending_id}
                      className="bg-[#1B1F3A] rounded-xl border border-[#2D3354] p-4"
                      data-testid={`spending-item-${item.spending_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-[#252A4A] flex items-center justify-center">
                            <ShoppingBag className="w-5 h-5 text-[#9CA3AF]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{item.description}</p>
                            <p className="text-[#6B7280] text-sm">
                              {new Date(item.date).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-semibold">-{formatCurrency(item.amount, currency)}</span>
                          <button
                            onClick={() => setEditingSpending(item)}
                            className="p-2 text-[#6B7280] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                            data-testid={`edit-spending-${item.spending_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeletingSpending(item)}
                            className="p-2 text-[#6B7280] hover:text-[#EF4444] hover:bg-[#EF4444]/10 rounded-lg transition-colors"
                            data-testid={`delete-spending-${item.spending_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#1B1F3A] border border-[#2D3354] flex items-center justify-center">
                <ShoppingBag className="w-8 h-8 text-[#6B7280]" />
              </div>
              <p className="text-white font-medium mb-1">No spending recorded</p>
              <p className="text-[#6B7280] text-sm">Add your first expense to track it</p>
            </div>
          )}
        </div>
      </main>

      {/* Mobile Navigation */}
      <MobileNav />

      {/* Add Spending Modal */}
      <AddSpendingModal 
        open={showAddModal} 
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchSpending();
          setShowAddModal(false);
        }}
      />

      {/* Edit Spending Modal */}
      <EditSpendingModal
        open={!!editingSpending}
        spending={editingSpending}
        onClose={() => setEditingSpending(null)}
        onSuccess={() => {
          fetchSpending();
          setEditingSpending(null);
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deletingSpending} onOpenChange={() => setDeletingSpending(null)}>
        <AlertDialogContent className="bg-[#1B1F3A] border-[#2D3354] text-white max-w-sm mx-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Spending?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9CA3AF]">
              Delete "{deletingSpending?.description}" for £{deletingSpending?.amount?.toFixed(2)}? This will add the amount back to your balance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#2D3354] text-white hover:bg-white/5">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-spending-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
